import * as apiPostProduct from 'api/erp/PostProduct';
import * as apiPost from 'api/orderGuide/PostErpOrderGuideV1';
import { Functor } from 'functions/Functor';
import * as OrderGuide from 'types/Ingredient/Detail/OrderGuide/OrderGuide';
import * as Product from 'types/Ingredient/Detail/OrderGuide/Product';
import { ProductList } from 'types/Ingredient/Detail/OrderGuide/ProductList';

export type Type = OrderGuide.Type[];

export function create(): Type {
	return [];
}

export async function getFromApiByIngredientId(ingredientId: string): Promise<Type> {
	const output = await Functor.From(ingredientId)
		.mapAsync((e) => postToApi([e]))
		.map(mapFromApiPost)
		.mapAsync(enrichWithProductInformation)
		.mapAsync((e) => enrichProductsWithIngredientId(e, ingredientId))
		.get();
	return output;
}

async function enrichProductsWithIngredientId(list: Type, ingredientId: string): Promise<Type> {
	return list.map((e) => ({
		...e,
		products: ProductList.enrichWithIngredientId(e.products, ingredientId),
	}));
}

async function postToApi(ingredientIds: string[]): Promise<apiPost.ResponseData> {
	const response = await apiPost.callApi(ingredientIds);
	return response.getOrDefault([]);
}

async function enrichWithProductInformation(list: Type): Promise<Type> {
	return await Functor.From(list)
		.map(getAllProductAndSourceIds)
		.map(distinct)
		.map(groupBySource)
		.mapAsync(ProductList.getFromApi)
		.map((products) => mergeWithProducts(list, products))
		.get();
}

async function mergeWithProducts(list: Type, products: ProductList.Type): Promise<Type> {
	return list.map((e) => OrderGuide.mergeWithProducts(e, products));
}

function getAllProductAndSourceIds(obj: Type): { productId: string; sourceId: string }[] {
	return obj.flatMap((e) =>
		e.products.map((p) => ({ productId: p.productId, sourceId: p.sourceId }))
	);
}

function distinct(
	list: { productId: string; sourceId: string }[]
): { productId: string; sourceId: string }[] {
	const output: { productId: string; sourceId: string }[] = [];
	for (const i of list) {
		if (output.some((e) => e.productId === i.productId && e.sourceId === i.sourceId)) continue;
		output.push(i);
	}
	return output;
}

function groupBySource(list: { productId: string; sourceId: string }[]): apiPostProduct.Request[] {
	const output: apiPostProduct.Request[] = [];
	for (const i of list) {
		const existing = output.find((e) => e.erpName === i.sourceId);
		if (existing) {
			existing.productIds.push(i.productId);
		} else {
			output.push({ erpName: i.sourceId, productIds: [i.productId] });
		}
	}
	return output;
}

function mapFromApiPost(input: apiPost.ResponseData): Type {
	return input.map((e) => OrderGuide.mapFromApiPostSearch(e));
}

export async function removeProduct(
	obj: Type,
	orderGuideId: string,
	productId: string
): Promise<Type> {
	const output: Type = [];
	for (const orderGuide of obj) {
		if (orderGuide.id !== orderGuideId) {
			output.push(orderGuide);
			continue;
		}
		output.push(await OrderGuide.removeProduct(orderGuide, productId));
	}
	return output;
}

export async function addProduct(obj: Type, product: Product.Type): Promise<Type> {
	const orderGuide: OrderGuide.Type = {
		...OrderGuide.create(),
		ingredientId: product.ingredientId,
		products: [product],
	};
	const newId: string = await OrderGuide.postToApi(orderGuide);
	return [...obj, { ...orderGuide, id: newId }];
}
export async function changePriority(
	obj: Type,
	input: {
		orderGuideId: string;
		productId: string;
		sourceId: string;
		priority: number;
	}
): Promise<Type> {
	const output: Type = [];
	for (const orderGuide of obj) {
		if (orderGuide.id !== input.orderGuideId) {
			output.push(orderGuide);
			continue;
		}
		output.push(await OrderGuide.changePriority(orderGuide, input));
	}
	return output;
}
